import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Clock, LogOut } from 'lucide-react';
import { api, canEdit, type SelfReportUpcomingEvent } from '@/lib/api';
import { cn } from '@/lib/utils';
import { SERVER_UTC_OFFSET_MINUTES } from '@/lib/serverTime';
import PlayerHubPicker from './PlayerHubPicker';
import PlayerHubPushButton from './PlayerHubPushButton';
import AllianceLogo from './AllianceLogo';
import { usePlayerAlliance } from '@/lib/usePlayerAlliance';

export interface PlayerHubHeaderProps {
  token: string;
  playerLabel: string;
  playerRank: string;
  subtitle?: string;
  upcomingEvents?: SelfReportUpcomingEvent[];
}

export default function PlayerHubHeader({
  token,
  playerLabel,
  playerRank,
  subtitle,
}: PlayerHubHeaderProps) {
  const base = `/players/${encodeURIComponent(token)}`;
  // Single source of truth so every hub page shows the same set of chips
  // (CS/DS + special). Callers may still pass `upcomingEvents` for backward
  // compat but the header now always fetches its own list.
  const upcomingQuery = useQuery({
    queryKey: ['player-upcoming', token],
    queryFn: () => api.getSelfReportMe(token),
    staleTime: 60_000,
    enabled: !!token,
    retry: false,
  });
  const events: SelfReportUpcomingEvent[] = upcomingQuery.data?.upcomingEvents ?? [];
  const alliance = usePlayerAlliance(token);
  return (
    <header className="border-b border-slate-800 bg-slate-900/60">
      <div className="mx-auto flex max-w-md items-center gap-3 px-4 py-3">
        <AllianceLogo alliance={alliance} className="h-10" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <div className="truncate text-base font-semibold">{playerLabel}</div>
            <BackendPlayerSwitcher />
          </div>
          <div className="text-xs text-slate-400">
            {playerRank}
            {subtitle ? ` · ${subtitle}` : ''}
          </div>
        </div>
        <ServerClock />
        <div className="flex shrink-0 items-center gap-1">
          <PlayerHubPushButton token={token} />
          <BackendAdminLink />
          <LogoutButton />
        </div>
      </div>
      {events.length > 0 && (
        <div className="mx-auto max-w-md px-4 pb-3">
          <UpcomingStrip events={events} token={token} />
        </div>
      )}
      <nav className="mx-auto flex max-w-md items-center px-4 pb-3">
        <div className="flex w-full rounded-lg border border-slate-800 bg-slate-950 p-0.5 text-sm">
          <TabLink to={`${base}/self-report`} label="Report" />
          <TabLink to={`${base}/stats`} label="Stats" />
          <TabLink to={`${base}/me`} label="History" />
          <TabLink to={`${base}/alliance`} label="Alliance" />
          <TabLink to={`${base}/hall-of-fame`} label="Fame" />
        </div>
      </nav>
    </header>
  );
}

function ServerClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const serverMs = now.getTime() + SERVER_UTC_OFFSET_MINUTES * 60_000;
  const s = new Date(serverMs);
  const sHh = String(s.getUTCHours()).padStart(2, '0');
  const sMm = String(s.getUTCMinutes()).padStart(2, '0');
  const sSs = String(s.getUTCSeconds()).padStart(2, '0');
  const lHh = String(now.getHours()).padStart(2, '0');
  const lMm = String(now.getMinutes()).padStart(2, '0');
  return (
    <div
      title="Game server time (UTC-2) / your local time"
      className="flex shrink-0 flex-col items-end text-right"
    >
      <div className="inline-flex items-center gap-1 text-sm font-semibold tabular-nums text-slate-100">
        <Clock size={12} className="text-slate-400" />
        {sHh}:{sMm}
        <span className="text-slate-500">:{sSs}</span>
      </div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">
        server
      </div>
      <div className="mt-0.5 text-[11px] font-medium tabular-nums text-slate-400">
        {lHh}:{lMm}{' '}
        <span className="text-[9px] uppercase tracking-wider text-slate-500">
          local
        </span>
      </div>
    </div>
  );
}

function useBackendSession() {
  return useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

function BackendPlayerSwitcher() {
  const meQuery = useBackendSession();
  // The picker lists every player's magic link — R4+ only.
  if (!meQuery.data || !canEdit(meQuery.data.role)) return null;
  return <PlayerHubPicker variant="icon" />;
}

function BackendAdminLink() {
  const meQuery = useBackendSession();
  if (!meQuery.data) return null;
  return (
    <NavLink
      to="/dashboard"
      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-800 bg-slate-950 text-slate-300 hover:text-slate-100"
      title="Back to administration"
      aria-label="Back to administration"
    >
      <ArrowLeft size={14} />
    </NavLink>
  );
}

function LogoutButton() {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.playerSessionLogout();
    } catch {
      // best-effort
    }
    window.location.assign('/login');
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-800 bg-slate-950 text-slate-300 hover:text-slate-100 disabled:opacity-50"
      title="Log out"
      aria-label="Log out"
    >
      <LogOut size={14} />
    </button>
  );
}

function UpcomingStrip({
  events,
  token,
}: {
  events: SelfReportUpcomingEvent[];
  token: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {events.map((e) => (
        <UpcomingRow key={e.eventId} event={e} token={token} />
      ))}
    </div>
  );
}

function UpcomingRow({
  event,
  token,
}: {
  event: SelfReportUpcomingEvent;
  token: string;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const startMs = Date.parse(event.startsAtUtc);
  const delta = startMs - now;

  if (event.type === 'train') {
    const isMega = event.trainKind === 'mega_express';
    const border = isMega
      ? 'border-emerald-500/70 bg-emerald-950/40 shadow-[0_0_0_1px_rgba(16,185,129,0.15)]'
      : 'border-sky-600/60 bg-sky-950/40';
    const textStrong = isMega ? 'text-emerald-100' : 'text-sky-100';
    const textSoft = isMega ? 'text-emerald-200' : 'text-sky-200';
    const badge = isMega ? 'MX' : 'TR';
    return (
      <Link
        to={`/players/${encodeURIComponent(token)}/alliance`}
        title={isMega ? 'Mega-Express — see alliance hub' : 'Train — see alliance hub'}
        className={cn(
          'group flex w-full items-start gap-3 rounded-lg border px-3 py-2 text-xs transition hover:brightness-110',
          border,
        )}
      >
        <TrainBadge kind={badge} isMega={isMega} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className={cn('truncate text-[11px] font-semibold', textStrong)}>
              {isMega ? 'Mega-Express' : 'Train'} · {event.conductor ?? '—'}
            </span>
            <span className={cn('whitespace-nowrap text-[11px] font-semibold tabular-nums', textStrong)}>
              {formatCountdown(delta)}
            </span>
          </div>
          <div className="text-[11px] text-slate-400">
            ETD {event.startsAtServer}
            {event.vipLabel && (
              <>
                {' · '}
                <span className={textSoft}>VIP: {event.vipLabel}</span>
              </>
            )}
          </div>
        </div>
      </Link>
    );
  }

  if (event.type === 'special_ingame') {
    const teamLabel =
      event.teamName?.trim() ||
      (event.teamIndex != null ? `Team ${event.teamIndex}` : 'Team');
    const title = event.customName?.trim() || 'Special event';
    return (
      <div className="flex w-full items-start gap-3 rounded-lg border border-fuchsia-800/60 bg-fuchsia-950/40 px-3 py-2 text-xs">
        <TypeBadge kind="SP" tone="fuchsia" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[11px] font-semibold text-fuchsia-100" title={title}>
              {teamLabel}
              <span className="ml-1 text-slate-400">· {title}</span>
            </span>
            <span className="whitespace-nowrap text-[11px] font-semibold tabular-nums text-fuchsia-100">
              {formatCountdown(delta)}
            </span>
          </div>
          <div className="text-[11px] text-slate-400">
            {event.eventDate}
            {event.startsAtServer !== '00:00' ? ` · ${event.startsAtServer}` : ''}
          </div>
          {event.teamTask && event.teamTask.trim() !== '' && (
            <div
              className="mt-0.5 truncate text-[11px] text-fuchsia-200"
              title={event.teamTask}
            >
              Task: {event.teamTask}
            </div>
          )}
        </div>
      </div>
    );
  }

  const short = event.type === 'canyon_storm' ? 'CS' : 'DS';
  const roleLabel =
    event.role === 'starter'
      ? 'Starter'
      : event.role === 'sub'
        ? 'Sub'
        : 'Slot';
  const counterpartLabel = event.counterpartName
    ? event.counterpartRole === 'sub'
      ? `Sub: ${event.counterpartName}`
      : event.counterpartRole === 'starter'
        ? `Starter: ${event.counterpartName}`
        : null
    : null;

  return (
    <Link
      to={`/players/${encodeURIComponent(token)}/events/${event.eventId}/swap`}
      title="Open swap coordination"
      className="group flex w-full items-start gap-3 rounded-lg border border-indigo-500/70 bg-indigo-950/40 px-3 py-2 text-xs shadow-[0_0_0_1px_rgba(99,102,241,0.15)] transition hover:border-indigo-400 hover:bg-indigo-900/60 hover:shadow-[0_0_0_1px_rgba(129,140,248,0.35)]"
    >
      <TypeBadge kind={short} tone="indigo" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[11px] font-semibold text-indigo-100">
            {roleLabel} · Slot {event.slot}
          </span>
          <span className="whitespace-nowrap text-[11px] font-semibold tabular-nums text-indigo-100">
            {formatCountdown(delta)}
          </span>
        </div>
        <div className="text-[11px] text-slate-400">
          {event.eventDate} · {event.startsAtServer}
          {counterpartLabel && (
            <>
              {' · '}
              <span className="text-slate-200">{counterpartLabel}</span>
            </>
          )}
        </div>
      </div>
      <ChevronRight
        size={14}
        className="mt-0.5 shrink-0 text-indigo-300 transition group-hover:translate-x-0.5 group-hover:text-indigo-100"
      />
    </Link>
  );
}

function TrainBadge({ kind, isMega }: { kind: 'TR' | 'MX'; isMega: boolean }) {
  return (
    <span
      className={cn(
        'mt-0.5 inline-flex h-6 w-8 shrink-0 items-center justify-center rounded border text-[10px] font-bold uppercase tracking-wider',
        isMega
          ? 'bg-emerald-900/70 text-emerald-100 border-emerald-500/60'
          : 'bg-sky-900/70 text-sky-100 border-sky-500/60',
      )}
    >
      {kind}
    </span>
  );
}

function TypeBadge({
  kind,
  tone,
}: {
  kind: 'CS' | 'DS' | 'SP';
  tone: 'indigo' | 'fuchsia';
}) {
  const toneClasses =
    tone === 'indigo'
      ? 'bg-indigo-900/70 text-indigo-100 border-indigo-500/50'
      : 'bg-fuchsia-900/70 text-fuchsia-100 border-fuchsia-500/50';
  return (
    <span
      className={cn(
        'mt-0.5 inline-flex h-6 w-8 shrink-0 items-center justify-center rounded border text-[10px] font-bold uppercase tracking-wider',
        toneClasses,
      )}
    >
      {kind}
    </span>
  );
}

function formatCountdown(deltaMs: number): string {
  if (deltaMs <= 0) return 'now';
  const totalSec = Math.floor(deltaMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
  if (days > 0) return `${days}d ${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  return `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}

function TabLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        cn(
          'flex-1 rounded px-1 py-1.5 text-center transition',
          isActive
            ? 'bg-indigo-600 text-white'
            : 'text-slate-400 hover:text-white',
        )
      }
    >
      {label}
    </NavLink>
  );
}
